<template>
  <div class="h-full">
    <div v-if="loading" class="flex-grow flex items-center justify-center h-full">
      <Spinner class="w-8" />
    </div>
    <div v-else class="w-full pr-3 sm:pr-10 pl-2 pb-2 max-w-1000">
      <div class="text-13 text-semi-dark-text mt-1">{{ $t('projects.encryption_keys.intro') }}</div>

      <div v-if="loadError" class="mt-4 border border-solid border-stroke rounded-8 p-4 flex items-center text-13">
        <Icon name="info" class="w-4 h-4 mr-2 flex-shrink-0 text-primary" />
        <span>{{ loadError }}</span>
      </div>

      <template v-else-if="keys">
        <div class="mt-6 text-18 font-500 mb-2">{{ $t('projects.encryption_keys.status_title') }}</div>
        <div class="border border-solid border-stroke rounded-8">
          <div class="flex justify-between items-center p-4 flex-wrap gap-3">
            <div v-if="activeVersion">
              <div class="flex items-center">
                <span class="font-600 mr-2">{{ $t('projects.encryption_keys.active_version', { version: activeVersion.version }) }}</span>
                <Badge success :text="$t('projects.encryption_keys.state_active')" />
              </div>
              <div class="text-12 text-gray-500">{{ $t('projects.encryption_keys.created_on', { date: formatDate(activeVersion.created_at) }) }}</div>
            </div>
            <div v-else class="text-13">{{ $t('projects.encryption_keys.no_active_key') }}</div>
            <div>
              <div class="text-12 text-gray-500">{{ $t('projects.encryption_keys.next_rotation') }}</div>
              <div class="font-600">{{ keys.next_rotation_at ? formatDate(keys.next_rotation_at) : $t('projects.encryption_keys.not_scheduled') }}</div>
            </div>
          </div>

          <div v-for="version in previousVersions" :key="version.version">
            <Divider dense />
            <div class="p-4">
              <div class="flex items-center">
                <span class="font-600 mr-2">{{ $t('projects.encryption_keys.previous_version', { version: version.version }) }}</span>
                <Badge warn :text="$t('projects.encryption_keys.state_decrypt_only')" />
              </div>
              <div v-if="version.destroy_after" class="text-12 text-gray-500">{{ $t('projects.encryption_keys.destroyed_on', { date: formatDate(version.destroy_after) }) }}</div>
            </div>
          </div>

          <template v-if="keys.job">
            <Divider dense />
            <div class="p-4">
              <div class="flex items-center flex-wrap gap-2">
                <Badge :error="keys.job.blocked" :warn="!keys.job.blocked && keys.job.attempts > 0" :info="!keys.job.blocked && !keys.job.attempts" :text="jobStateLabel" />
                <span class="font-600">{{ jobDescription }}</span>
              </div>
              <div class="text-12 text-gray-500 mt-1">
                {{ $t('projects.encryption_keys.job_started', { date: formatDate(keys.job.started_at) }) }}
                <template v-if="keys.job.attempts"> · {{ $t('projects.encryption_keys.job_attempts', { attempts: keys.job.attempts }) }}</template>
              </div>
              <div v-if="keys.job.blocked" class="text-13 text-alert mt-2">{{ $t('projects.encryption_keys.job_blocked_help') }}</div>
              <div v-else-if="keys.job.attempts" class="text-13 mt-2">{{ $t('projects.encryption_keys.job_retrying_help') }}</div>
            </div>
          </template>

          <Divider dense />
          <div class="flex justify-between items-center p-4 flex-wrap gap-3">
            <div>
              <div class="font-600">{{ $t('projects.encryption_keys.rotate_now') }}</div>
              <div class="text-13">{{ $t('projects.encryption_keys.rotate_description') }}</div>
              <div v-if="rotateResult" class="text-13 mt-2" :class="{ 'text-green-600': rotateResult.status === 'completed', 'text-alert': rotateResult.status !== 'completed' }">
                {{ rotateResultText }}
              </div>
              <FormError v-if="rotateError" :label="rotateError" />
            </div>
            <PillButton primary icon="refresh" :text="$t('projects.encryption_keys.rotate_now')" :loading="rotating" :disabled="rotating" @click="onRotateClick" />
          </div>
        </div>

        <div class="mt-8 text-18 font-500 mb-2">{{ $t('projects.encryption_keys.policy_title') }}</div>
        <div class="w-300 max-w-full">
          <FormInput
            v-model="rotationDays"
            type="number"
            :min="String(POLICY_LIMITS.rotation_days[0])"
            :max="String(POLICY_LIMITS.rotation_days[1])"
            :label="$t('projects.encryption_keys.rotation_days')"
            name="rotationDays"
            :error="rotationDaysError"
            @update:modelValue="rotationDaysError = ''"
          />
        </div>
        <div class="text-12 text-semi-dark-text mt-1 max-w-600">{{ $t('projects.encryption_keys.rotation_days_help') }}</div>
        <div class="w-300 max-w-full mt-3">
          <FormInput
            v-model="graceDays"
            type="number"
            :min="String(POLICY_LIMITS.grace_days[0])"
            :max="String(POLICY_LIMITS.grace_days[1])"
            :label="$t('projects.encryption_keys.grace_days')"
            name="graceDays"
            :error="graceDaysError"
            @update:modelValue="graceDaysError = ''"
          />
        </div>
        <div class="text-12 text-semi-dark-text mt-1 max-w-600">{{ $t('projects.encryption_keys.grace_days_help') }}</div>
        <FormError v-if="policyError" :label="policyError" />
        <div class="flex items-center mt-4 flex-wrap gap-3">
          <PillButton primary :text="$t('save')" :loading="savingPolicy" :disabled="savingPolicy" @click="onSavePolicyClick" />
          <span v-if="keys.policy?.updated_at && keys.policy?.updated_by" class="text-12 text-gray-500">
            {{ $t('projects.encryption_keys.policy_updated_by', { actor: keys.policy.updated_by, date: formatDate(keys.policy.updated_at) }) }}
          </span>
        </div>

        <div class="mt-8 text-18 font-500 mb-2">{{ $t('projects.encryption_keys.history_title') }}</div>
        <div v-if="!historyItems.length" class="text-13 text-gray-500">{{ $t('projects.encryption_keys.history_empty') }}</div>
        <TableElevated v-else :items="historyItems" :headers="historyHeaders" class="text-13">
          <template v-slot:[`item.details`]="{ item }">
            <div class="py-1">
              <div>{{ item.details }}</div>
              <div v-if="item.unreadable" class="text-12 text-alert">{{ item.unreadable }}</div>
            </div>
          </template>
        </TableElevated>
      </template>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { mapActions, mapGetters } from 'vuex';
import DeleteModal from '../../../modals/DeleteModal.vue';

const POLICY_LIMITS = { rotation_days: [1, 365], grace_days: [1, 90] };
const DEFAULT_POLICY = { rotation_days: 30, grace_days: 7 };
const UNREADABLE_SHOWN = 3;
const KNOWN_EVENTS = [
  'key.created',
  'key.rotated',
  'key.destroyed',
  'key.rewrapped',
  'policy.updated',
  'rotation.completed',
  'rotation.incomplete',
  'rotation.blocked',
  'migration.completed',
  'migration.incomplete',
  'migration.blocked',
  'creation.completed',
];

export default {
  name: 'EncryptionKeys',
  data() {
    return {
      POLICY_LIMITS,
      loading: true,
      loadError: '',
      rotationDays: String(DEFAULT_POLICY.rotation_days),
      graceDays: String(DEFAULT_POLICY.grace_days),
      rotationDaysError: '',
      graceDaysError: '',
      policyError: '',
      savingPolicy: false,
      rotating: false,
      rotateResult: null,
      rotateError: '',
    };
  },
  computed: {
    ...mapGetters(['getProject', 'isProjectOwner', 'getProjectEncryptionKeys']),
    /**
     * @returns {string} The project id from the route.
     */
    projectId() {
      return this.$route.params.projectId;
    },
    /**
     * Whether the signed-in user owns the project; null until the team members are loaded.
     * @returns {(boolean|null)} Owner state.
     */
    ownerCheck() {
      if (!this.getProject(this.projectId).teamMembers) return null;
      return this.isProjectOwner(this.projectId);
    },
    /**
     * @returns {(object|null)} The last loaded key status.
     */
    keys() {
      return this.getProjectEncryptionKeys(this.projectId);
    },
    /**
     * @returns {(object|undefined)} The active key version.
     */
    activeVersion() {
      return (this.keys?.versions || []).find((v) => v.state === 'active');
    },
    /**
     * @returns {Array<object>} Older versions that can still decrypt, newest first.
     */
    previousVersions() {
      return (this.keys?.versions || []).filter((v) => v.state === 'decrypt_only').sort((a, b) => b.version - a.version);
    },
    /**
     * @returns {string} Badge text for the running key job.
     */
    jobStateLabel() {
      const { job } = this.keys;
      if (job.blocked) return this.$t('projects.encryption_keys.job_blocked');
      if (job.attempts) return this.$t('projects.encryption_keys.job_retrying');
      return this.$t('projects.encryption_keys.job_in_progress');
    },
    /**
     * @returns {string} What the running key job does.
     */
    jobDescription() {
      const { kind, to_version } = this.keys.job;
      const key = ['migration', 'creation'].includes(kind) ? kind : 'rotation';
      return this.$t(`projects.encryption_keys.job_${key}`, { version: to_version });
    },
    /**
     * @returns {string} Readable outcome of the last "Rotate now".
     */
    rotateResultText() {
      const { status } = this.rotateResult;
      const key = ['completed', 'incomplete', 'blocked'].includes(status) ? status : 'incomplete';
      return this.$t(`projects.encryption_keys.rotate_${key}`, { version: this.activeVersion?.version ?? '' });
    },
    /**
     * @returns {Array<object>} Table headers for the history.
     */
    historyHeaders() {
      return [
        { key: 'event', title: this.$t('projects.encryption_keys.history_event'), width: '200px' },
        { key: 'details', title: this.$t('projects.encryption_keys.history_details') },
        { key: 'actor', title: this.$t('projects.encryption_keys.history_actor'), width: '200px' },
        { key: 'when', title: this.$t('projects.encryption_keys.history_when'), width: '160px' },
      ];
    },
    /**
     * @returns {Array<object>} History rows, newest first.
     */
    historyItems() {
      return [...(this.keys?.events || [])]
        .sort((a, b) => b.at - a.at)
        .map((e, index) => ({
          key: index,
          event: this.eventLabel(e.event),
          details: this.summarizeDetail(e.detail),
          unreadable: this.summarizeUnreadable(e.detail),
          actor: e.actor || this.$t('projects.encryption_keys.schedule'),
          when: this.formatDate(e.at),
        }));
    },
  },
  methods: {
    ...mapActions(['fetchProjectKeys', 'updateProjectKeyPolicy', 'rotateProjectKeys', 'showToastMessage']),
    /**
     * Formats a millisecond timestamp the way the rest of the studio does.
     * @param {number} value Timestamp in milliseconds.
     * @returns {string} e.g. "Oct 11, 2026 14:05", or an empty string.
     */
    formatDate(value) {
      return value ? dayjs(value).format('MMM DD, YYYY HH:mm') : '';
    },
    /**
     * @param {string} event Event name, e.g. key.rotated.
     * @returns {string} Readable label, or the name itself for an unknown event.
     */
    eventLabel(event) {
      return KNOWN_EVENTS.includes(event) ? this.$t(`projects.encryption_keys.events.${event.replace('.', '_')}`) : event;
    },
    /**
     * Turns an event's detail into a short readable summary.
     * @param {(object|null)} detail Event detail.
     * @returns {string} Summary, or an empty string.
     */
    summarizeDetail(detail) {
      if (!detail) return '';
      const t = (key, values) => this.$t(`projects.encryption_keys.detail.${key}`, values);
      if (detail.rotation_days !== undefined) return t('policy', detail);
      if (Array.isArray(detail.versions)) return t('versions', { versions: detail.versions.join(', ') });
      if (detail.version !== undefined) {
        const parts = [t('version', detail)];
        if (detail.previous) parts.push(t('replaced', detail));
        if (['manual', 'schedule'].includes(detail.reason)) parts.push(t(`reason_${detail.reason}`));
        return parts.join(', ');
      }

      const parts = [];
      if (detail.to_version) parts.push(t('version', { version: detail.to_version }));
      const remaining = Object.values(detail.remaining || {}).reduce((sum, n) => sum + n, 0) + (detail.legacy_remaining || 0);
      const counts = { reencrypted: detail.reencrypted, legacy_converted: detail.legacy_converted, conflicts: detail.conflicts, remaining, unreadable: detail.unreadable?.length };
      const countParts = Object.entries(counts)
        .filter(([, count]) => count > 0)
        .map(([key, count]) => t(key, { count }));
      if (!countParts.length && detail.attempts !== undefined) countParts.push(t('nothing'));
      parts.push(...countParts);
      if (detail.attempts > 1) parts.push(t('attempt', detail));
      return parts.join(' · ');
    },
    /**
     * Lists the first few secrets that could not be read, so the owner knows what to fix.
     * @param {(object|null)} detail Event detail.
     * @returns {string} e.g. "Could not read: a, b, c (+2 more)", or an empty string.
     */
    summarizeUnreadable(detail) {
      const items = detail?.unreadable;
      if (!Array.isArray(items) || !items.length) return '';
      const shown = items.slice(0, UNREADABLE_SHOWN).join(', ');
      const more = items.length > UNREADABLE_SHOWN ? ` (${this.$t('projects.encryption_keys.detail.more', { count: items.length - UNREADABLE_SHOWN })})` : '';
      return this.$t('projects.encryption_keys.detail.unreadable_list', { items: shown + more });
    },
    /**
     * Fills the policy form from the loaded policy.
     * @param {(object|undefined)} policy Saved policy.
     * @returns {void}
     */
    fillPolicyForm(policy) {
      this.rotationDays = String(policy?.rotation_days ?? DEFAULT_POLICY.rotation_days);
      this.graceDays = String(policy?.grace_days ?? DEFAULT_POLICY.grace_days);
      this.rotationDaysError = '';
      this.graceDaysError = '';
      this.policyError = '';
    },
    /**
     * Loads the key status for the current project.
     * @returns {Promise<void>} Resolves once loaded or failed.
     */
    async loadKeys() {
      this.loading = true;
      this.loadError = '';
      this.rotateResult = null;
      this.rotateError = '';
      const { keys, error } = await this.fetchProjectKeys({ project_id: this.projectId });
      this.loadError = error || '';
      if (keys) this.fillPolicyForm(keys.policy);
      this.loading = false;
    },
    /**
     * Reads a whole number of days within limits.
     * @param {string} raw Field value.
     * @param {Array<number>} limits [min, max].
     * @returns {{value?: number, error?: string}} The number, or a message for the field.
     */
    readDays(raw, [min, max]) {
      const value = Number(String(raw ?? '').trim());
      if (String(raw ?? '').trim() === '' || !Number.isInteger(value) || value < min || value > max) {
        return { error: this.$t('projects.encryption_keys.days_range', { min, max }) };
      }
      return { value };
    },
    /**
     * Validates and saves the rotation policy, showing problems next to the form.
     * @returns {Promise<void>} Resolves once saved or rejected.
     */
    async onSavePolicyClick() {
      this.policyError = '';
      const rotation = this.readDays(this.rotationDays, POLICY_LIMITS.rotation_days);
      const grace = this.readDays(this.graceDays, POLICY_LIMITS.grace_days);
      this.rotationDaysError = rotation.error || '';
      this.graceDaysError = grace.error || '';
      if (rotation.error || grace.error) return;

      this.savingPolicy = true;
      const { policy, error } = await this.updateProjectKeyPolicy({ project_id: this.projectId, rotation_days: rotation.value, grace_days: grace.value });
      this.savingPolicy = false;
      if (error) {
        this.policyError = error;
        return;
      }
      this.fillPolicyForm(policy);
      this.showToastMessage({ message: this.$t('projects.encryption_keys.policy_saved'), type: 'success' });
    },
    /**
     * Asks for confirmation, then rotates the key and shows the outcome inline.
     * @returns {void}
     */
    onRotateClick() {
      this.$showModal(DeleteModal, {
        title: this.$t('projects.encryption_keys.rotate_confirm_title'),
        subtitle: this.$t('projects.encryption_keys.rotate_confirm_subtitle', { grace_days: this.keys?.policy?.grace_days ?? DEFAULT_POLICY.grace_days }),
        confirm: this.$t('projects.encryption_keys.rotate_now'),
        type: 'primary',
        onConfirm: async () => {
          this.rotating = true;
          this.rotateResult = null;
          this.rotateError = '';
          const { status, error } = await this.rotateProjectKeys({ project_id: this.projectId });
          this.rotating = false;
          if (error) this.rotateError = error;
          else this.rotateResult = { status };
        },
      });
    },
    /**
     * Sends non-owners back to the general settings; loads the keys for owners.
     * @returns {void}
     */
    onAccessChange() {
      if (this.ownerCheck === false) {
        this.$router.replace({ name: 'project-settings', params: this.$route.params });
      } else if (this.ownerCheck) {
        this.loadKeys();
      }
    },
  },
  watch: {
    projectId: {
      handler() {
        this.onAccessChange();
      },
      immediate: true,
    },
    ownerCheck() {
      this.onAccessChange();
    },
  },
};
</script>
